import React from "react";
import {Input} from "reactstrap";
import {connect} from "react-redux";
import "react-dates/initialize";
import {SingleDatePicker} from "react-dates";
import moment from "moment";
import {
    getCurrentNewsletterAvailableArchiveDates,
    isCurrentNewsletterTemporary,
    getIsNewsletterHidden,
    getCurrentNewsletterEdition,
} from "../../../../redux/selectors/newsletters";
import {getUrlDate} from "../../../../redux/selectors/navigation";
import {navigateToNewsletterEdition} from "../../../../redux/actions/navigation";
import {clearSearch} from "../../../../redux/actions/search";
import {isSearchActive} from "../../../../redux/selectors/search";

/**
 * output a calendar/day picker or the current newsletter date string
 * @param {Array} props.availableArchiveDates - array of all available editions for this newsletter
 */
class EditionCalendar extends React.Component {
    constructor (props) {
        super(props);

        //we don't have newsletters dated in the future
        this.firstVisibleDate = moment();

        //we only show a year's worth of editions
        this.lastVisibleDate = moment().subtract(24, "months");

        this.state = {
            focused: false,
            isPrevMonthDisabled: false,
            isNextMonthDisabled: false,
        };
    }

    /**
     * return the icon used for the calendar
     * @return {domNode}
     */
    getIcon() {
        return <i className="far fa-calendar-alt"></i>;
    }

    /**
     * when a user navigates back we should check if the next previous month should disable
     * @param  {Moment} dateClicked - moment object represneting the month we are navigating to
     */
    onPrevMonthClick = dateClicked => {
        this.setState({
            isPrevMonthDisabled: moment(dateClicked, "month").isSame(this.lastVisibleDate, "month"),
            isNextMonthDisabled: false,
        });
    }

    /**
     * when a user navigates forward we should check if the next month should disable
     * @param  {Moment} dateClicked - moment object represneting the month we are navigating to
     */
    onNextMonthClick = dateClicked => {
        this.setState({
            isNextMonthDisabled: moment(dateClicked, "month").isSame(this.firstVisibleDate, "month"),
            isPrevMonthDisabled: false,
        });
    }

    /**
     * the calendar marks days as blocked/disabled;
     * search the list of archive dates for the given day
     * @param  {Moment} day - check if this day is blocked
     * @return {Boolean} - true if the day is blocked
     */
    isDayBlocked = day => {
        const isAvailable = this.props.availableArchiveDates.some(date => day.isSame(date, "day"));

        return !isAvailable;
    }

    /**
     * are any days outside a time/date period? if so they will be blocked
     * by default the calerndar blocks all past days. this will make sure they are enabled instead
     * @return {Boolean} - true if the date is outside a range;  we don't want to block anything so always false
     */
    isOutsideRange = () => false;

    /**
     * handle the focus change for the calendar; requires we set state to handle open/closed view
     * @param  {Boolean}
     */
    focusChange = ({focused}) => {
        let shouldDisableNext = false;

        let shouldDisablePrev = false;

        if (focused === true) {
            //the calendar will be shown. let's make sure the month nav is disabled as necessary
            const monthDate = moment(this.props.currentDataEdition);

            shouldDisableNext = moment(monthDate, "month")
                                    .isSame(this.firstVisibleDate, "month");

            shouldDisablePrev = moment(monthDate, "month")
                                    .isSame(this.lastVisibleDate, "month");

        }

        this.setState({
            focused,
            isNextMonthDisabled: shouldDisableNext,
            isPrevMonthDisabled: shouldDisablePrev,
        });
    }

    /**
     * handle calendar change event
     * @param  {Moment} momentObj - date as moment object
     */
    handleDateChange = momentObj => {
        if (this.props.searchIsActive) {
            this.props.clearSearch();
        }

        if (moment(this.props.currentDataEdition).isSame(momentObj, "day")) {
            return;
        }

        const formattedIncomingDate = momentObj.format("YYYY-MM-DD");

        this.props.changeNewsletteDate({date: formattedIncomingDate});
    }

    getCurrentDate = ({currentDataEdition, urlDate}) => {
        if (currentDataEdition !== "") {
            return moment(currentDataEdition);
        } else if (urlDate !== "") {
            return moment(urlDate);
        } else {
            return null;
        }
    }

    /**
     * render content
     */
    render() {
        const {
            //currentDataEdition,
            isTemporary,
            isNewsletterHidden,
            //urlDate,
        } = this.props;

        const displayDateFormat = "dddd, MMMM D";

        const currentDate = this.getCurrentDate(this.props);

        if ((isTemporary || isNewsletterHidden) && currentDate) {
            {/* temporary output text only */}
            return (
                <div className="w-100 pb-3 px-3">
                    <i className="far fa-calendar-alt single-newsletter-icon"></i>
                    {currentDate.format(displayDateFormat)}
                </div>
            )
        } else {
        {/* output a calendar with the available edition dates */}
            const prevBtnClass = this.state.isPrevMonthDisabled ? "disabledPrevBtn" : "";

            const nextBtnClass = this.state.isNextMonthDisabled ? "disabledNextBtn" : "";

            return (
                <div className={`input-group w-100 pb-3 px-3 ${prevBtnClass} ${nextBtnClass}`}>
                    <SingleDatePicker
                        date={currentDate} // momentPropTypes.momentObj or null
                        onDateChange={this.handleDateChange} // PropTypes.func.isRequired
                        focused={this.state.focused} // PropTypes.bool
                        onFocusChange={this.focusChange} // PropTypes.func.isRequired
                        id="singleDatePicker" // PropTypes.string.isRequired
                        numberOfMonths={1} //show only one calendar
                        block={true} //force full width
                        small={true}
                        noBorder={true}
                        hideKeyboardShortcutsPanel={true}
                        customInputIcon={this.getIcon()}
                        verticalSpacing={0}
                        displayFormat={displayDateFormat}
                        readOnly={true}

                        onPrevMonthClick={this.onPrevMonthClick}
                        onNextMonthClick={this.onNextMonthClick}

                        isOutsideRange={this.isOutsideRange}
                        isDayBlocked={this.isDayBlocked}

                    />
                </div>
            )
        }
    }
}

/**
 * mapStateToProps returns the parts of the state that will be available as props
 * @param state
 * @returns {}
 */
const mapStateToProps = state => {
    return {
        availableArchiveDates: getCurrentNewsletterAvailableArchiveDates(state),
        currentDataEdition: getCurrentNewsletterEdition(state),
        isTemporary: isCurrentNewsletterTemporary(state),
        searchIsActive: isSearchActive(state),
        isNewsletterHidden: getIsNewsletterHidden(state),
        urlDate: getUrlDate(state),
    };
};

/**
 * mapDispatchToProps exposes the actions we want to call as props
 * @param state
 * @returns {}
 */
const mapDispatchToProps = dispatch => {
    return {
        changeNewsletteDate: params => {
            dispatch(navigateToNewsletterEdition(params))
        },
        clearSearch: (isActive) => {
            dispatch(clearSearch())
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(EditionCalendar);
